<template>
  <article class="page-press" :lang="zh ? 'zh-Hant' : 'en'">
    <header class="press-heading">
      <p class="press-kicker">CHE-YU WU / PRESS</p>
      <h1>{{ text.title }}</h1>
      <div class="press-heading-bottom">
        <p>{{ text.intro }}</p>
        <a href="#press-archive"
          >{{ text.archive }} <span>{{ records.length }}</span> ↓</a
        >
      </div>
    </header>

    <section class="press-opening" :aria-label="text.selected">
      <figure class="press-portrait">
        <SelectedPhoto id="taiwan-md-talk" eager />
        <figcaption>{{ zh ? 'Taiwan.md 發表演講' : 'Presenting Taiwan.md' }}</figcaption>
      </figure>
      <div class="press-featured">
        <p class="press-kicker">01 / {{ text.selected }}</p>
        <a
          class="press-lead"
          :href="lead.url"
          target="_blank"
          rel="noopener noreferrer"
          ><span class="press-source">{{ lead.source }} · {{ lead.date }}</span>
          <h2>{{ lead.title }}</h2>
          <p>{{ localized(lead, "excerpt") }}</p>
          <span class="press-action">{{ text.watch }} ↗︎</span></a
        >
        <a
          v-for="item in secondary"
          :key="item.id"
          class="press-secondary"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          ><div class="press-secondary-text">
            <span class="press-source"
              >{{ localized(item, "source") }} · {{ item.date }}</span
            >
            <h3 :lang="titleLanguage(item)">{{ localized(item, "title") }}</h3>
            <span class="press-action"
              >{{ item.format === "audio" ? text.listen : text.read }} ↗︎</span
            >
          </div>
          <img
            class="press-secondary-thumb"
            :src="item.thumb"
            alt=""
            loading="lazy"
            decoding="async"
            width="720"
            height="480"
        /></a>
      </div>
    </section>

    <section id="press-archive" class="press-archive">
      <div class="press-archive-heading">
        <p class="press-kicker">02 / {{ text.archive }}</p>
        <p>{{ text.archiveNote }}</p>
      </div>
      <div class="press-archive-layout">
        <aside class="press-tools" :aria-label="text.filters">
          <label class="press-search"
            ><span>{{ text.search }}</span
            ><input
              type="search"
              v-model="query"
              :placeholder="text.placeholder"
          /></label>
          <fieldset>
            <legend>{{ text.format }}</legend>
            <button
              v-for="format in formats"
              :key="format"
              type="button"
              :aria-pressed="activeFormat === format"
              @click="activeFormat = format"
            >
              {{ text.formats[format]
              }}<span>{{
                format === "all"
                  ? records.length
                  : records.filter((r) => matchesFormat(r, format)).length
              }}</span>
            </button>
          </fieldset>
          <label class="press-kind-filter"
            ><span>{{ text.contentKind }}</span
            ><select v-model="activeKind">
              <option value="all">{{ text.allKinds }}</option>
              <option v-for="kind in kinds" :key="kind" :value="kind">
                {{ text.kinds[kind] }}
              </option>
            </select></label
          >
          <label class="press-year"
            ><span>{{ text.year }}</span
            ><select v-model="year">
              <option value="">{{ text.allYears }}</option>
              <option v-for="value in years" :key="value" :value="value">
                {{ value }}
              </option>
            </select></label
          >
          <button
            v-if="hasFilters"
            type="button"
            class="press-reset"
            @click="reset"
          >
            {{ text.reset }} ×
          </button>
          <p class="press-result-count" role="status" aria-live="polite">
            {{ filtered.length }} / {{ records.length }} {{ text.records }}
          </p>
        </aside>
        <div class="press-results">
          <div v-if="!filtered.length" class="press-empty">
            <h2>{{ text.empty }}</h2>
            <p>{{ text.emptyHelp }}</p>
            <button type="button" @click="reset">{{ text.reset }} →</button>
          </div>
          <section
            v-for="group in groups"
            :key="group.year"
            class="press-year-group"
            :aria-labelledby="'press-year-' + group.year"
          >
            <h2 :id="'press-year-' + group.year" class="press-year-heading">
              <span>{{ group.year }}</span
              ><small>{{ group.count }} {{ text.records }}</small>
            </h2>
            <article
              v-for="item in group.items"
              :key="item.id"
              class="press-record"
              :id="'press-' + item.id"
              :data-press-id="item.id"
            >
              <div class="press-record-main">
                <div class="press-record-meta">
                  <span>{{ item.publisher || localized(item, "source") }}</span
                  ><span class="press-kind">{{ text.kinds[item.kind] }}</span>
                </div>
                <h3>
                  <a
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    :lang="titleLanguage(item)"
                    >{{ localized(item, "title") }}
                    <span aria-hidden="true">↗︎</span></a
                  >
                </h3>
                <p :lang="bodyLanguage(item)">
                  {{ localized(item, "excerpt") }}
                </p>
                <small v-if="item.language !== 'unspecified'"
                  >{{ text.sourceLanguage }}:
                  {{
                    item.language === "bilingual"
                      ? text.bilingual
                      : item.language === "en"
                        ? text.english
                        : text.chinese
                  }}</small
                >
                <div v-if="item.seriesMembers" class="press-series">
                  <p class="press-series-label">
                    {{ text.seriesLabel }}
                    <span v-if="item.seriesTitle">{{
                      zh ? item.seriesTitle.zh : item.seriesTitle.en || item.seriesTitle.zh
                    }}</span>
                    · {{ item.seriesMembers.length + 1 }} {{ text.records }}
                  </p>
                  <ul>
                    <li
                      v-for="m in item.seriesMembers"
                      :key="m.id"
                      :id="'press-' + m.id"
                      :data-press-id="m.id"
                    >
                      <a :href="m.url" target="_blank" rel="noopener noreferrer"
                        ><img
                          v-if="m.thumb"
                          :src="m.thumb"
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width="720"
                          height="480"
                          :class="{ 'is-contain': m.thumbFit === 'contain' }" /><span
                          ><b>{{ m.publisher || localized(m, "source") }}</b
                          ><time :datetime="m.date">{{ m.date }}</time
                          ><em :lang="titleLanguage(m)">{{ localized(m, "title") }}</em></span
                        ></a
                      >
                    </li>
                  </ul>
                </div>
                <details
                  v-if="!zh && /[\u3400-\u9fff]/.test(item.title)"
                  class="press-original-title"
                >
                  <summary>{{ text.originalTitle }}</summary>
                  <p lang="zh-Hant">{{ item.title }}</p>
                </details>
              </div>
              <div class="press-record-aside">
                <a
                  v-if="item.thumb"
                  class="press-record-media"
                  :class="{ 'is-contain': item.thumbFit === 'contain' }"
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabindex="-1"
                  aria-hidden="true"
                  :title="item.thumbCredit && (zh ? '圖：' : 'Image: ') + item.thumbCredit"
                  ><img
                    :src="item.thumb"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="720"
                    height="480"
                /></a>
                <p class="press-record-date">
                  <time :datetime="item.date">{{ item.date }}</time
                  ><span>{{
                    item.additionalFormats
                      ? text.articleVideo
                      : text.formats[item.format]
                  }}</span>
                </p>
              </div>
            </article>
          </section>
        </div>
      </div>
    </section>

    <section class="press-resources">
      <div>
        <p class="press-kicker">03 / {{ text.resources }}</p>
        <h2>{{ text.resourceTitle }}</h2>
        <p>{{ text.resourceNote }}</p>
      </div>
      <nav :aria-label="text.resources">
        <router-link :to="local('/about/')">{{ text.about }} →</router-link
        ><router-link :to="local('/cv/')">{{ text.cv }} →</router-link
        ><router-link :to="local('/dialogues/')"
          >{{ text.dialogues }} →</router-link
        ><router-link :to="local('/contact/')"
          >{{ text.contact }} →</router-link
        >
      </nav>
    </section>
  </article>
</template>
<script setup>
import { withSlash } from '@/runtime/site-path.mjs'
import SelectedPhoto from "@/components/photos/SelectedPhoto.vue";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  records,
  filterPress,
  matchesFormat,
  aggregateSeries,
} from "@/components/press/press-records.mjs";
const route = useRoute(),
  router = useRouter(),
  zh = computed(() => route.path.startsWith("/zh/"));
const local = (p) => withSlash((zh.value ? "/zh" : "") + p);
const strings = {
  zh: {
    title: "報導與訪談",
    intro: "透過媒體的提問與記錄，觀看作品如何走進不同的語境。",
    archive: "完整媒體檔案",
    selected: "精選閱讀與觀看",
    imageNote: "藝術家檔案照片",
    watch: "觀看紀錄片",
    listen: "收聽訪談",
    read: "閱讀報導",
    archiveNote: "保留來源原有標題與日期精度。外部內容在原發布平台開啟。",
    filters: "篩選媒體檔案",
    contentKind: "內容類型",
    allKinds: "全部類型",
    search: "搜尋",
    placeholder: "標題、媒體或關鍵字",
    format: "媒體形式",
    articleVideo: "文章與影片",
    bilingual: "中文與英語",
    formats: { all: "全部", article: "文章", audio: "聲音訪談", film: "影片" },
    year: "年份",
    allYears: "全部年份",
    reset: "清除篩選",
    records: "筆紀錄",
    empty: "沒有符合的紀錄",
    emptyHelp: "試試其他關鍵字，或清除年份與形式篩選。",
    kinds: {
      reporting: "媒體報導",
      interview: "訪談",
      collaboration: "品牌合作內容",
      institution: "機構紀錄",
      column: "藝術家署名專欄",
    },
    seriesLabel: "同系列報導",
    sourceLanguage: "原始內容語言",
    english: "英語",
    chinese: "中文",
    unspecified: "未註明",
    originalTitle: "保留中文原標題",
    resources: "藝術家資料",
    resourceTitle: "採訪前，也可以從這裡開始。",
    resourceNote:
      "藝術家自述與對話另列，與本頁外部媒體記錄區分。影像使用與採訪需求請直接聯繫。",
    about: "藝術家簡介",
    cv: "完整履歷",
    dialogues: "原始對話與筆記",
    contact: "採訪與影像洽詢",
  },
  en: {
    title: "Press & conversations",
    intro:
      "A view of the work through the questions and perspectives of publications, interviewers and filmmakers.",
    archive: "Complete archive",
    selected: "Selected reading & viewing",
    imageNote: "Artist archive photograph",
    watch: "Watch the documentary",
    listen: "Listen to the interview",
    read: "Read the story",
    archiveNote:
      "Original titles and available date precision are preserved. Links open on the original publishing platform.",
    filters: "Filter media archive",
    contentKind: "Content type",
    allKinds: "All types",
    search: "Search",
    placeholder: "Title, publication, or keyword",
    format: "Format",
    articleVideo: "Article & video",
    bilingual: "Chinese & English",
    formats: {
      all: "All",
      article: "Articles",
      audio: "Audio interviews",
      film: "Film & video",
    },
    year: "Year",
    allYears: "All years",
    reset: "Clear filters",
    records: "records",
    empty: "No matching records",
    emptyHelp: "Try another keyword, or clear the year and format filters.",
    kinds: {
      reporting: "Media coverage",
      interview: "Interview",
      collaboration: "Brand collaboration",
      institution: "Institutional record",
      column: "Artist-authored column",
    },
    seriesLabel: "In this series",
    sourceLanguage: "Source language",
    english: "English",
    chinese: "Chinese",
    unspecified: "Not specified",
    originalTitle: "Original Chinese title",
    resources: "Artist resources",
    resourceTitle: "Before a conversation, start here.",
    resourceNote:
      "Artist statements and original dialogues are listed separately from external media coverage. Please get in touch about interviews and image use.",
    about: "About the artist",
    cv: "Full CV",
    dialogues: "Original dialogues & notes",
    contact: "Press & image enquiries",
  },
};
const text = computed(() => strings[zh.value ? "zh" : "en"]);
const formats = ["all", "article", "audio", "film"],
  years = [...new Set(records.map((r) => r.date.slice(0, 4)))].sort().reverse();
const queryValue = (key) =>
  typeof route.query[key] === "string" ? route.query[key] : "";
const change = (key, value) =>
  router.replace({
    path: route.path,
    query: { ...route.query, [key]: value || undefined },
    hash: route.hash,
  });
const query = computed({
  get: () => queryValue("q"),
  set: (value) => change("q", value),
});
const year = computed({
  get: () => (years.includes(queryValue("year")) ? queryValue("year") : ""),
  set: (value) => change("year", value),
});
const activeFormat = computed({
  get: () =>
    formats.includes(queryValue("format")) ? queryValue("format") : "all",
  set: (value) => change("format", value === "all" ? "" : value),
});
const kinds = [
  "interview",
  "reporting",
  "collaboration",
  "institution",
  "column",
];
const activeKind = computed({
  get: () => (kinds.includes(queryValue("kind")) ? queryValue("kind") : "all"),
  set: (value) => change("kind", value === "all" ? "" : value),
});
const hasFilters = computed(
  () =>
    !!query.value ||
    !!year.value ||
    activeFormat.value !== "all" ||
    activeKind.value !== "all",
);
const filtered = computed(() =>
  filterPress(records, {
    query: query.value,
    year: year.value,
    format: activeFormat.value,
    kind: activeKind.value,
  }),
);
// 打字搜尋時攤平成單篇；瀏覽時同系列收在代表底下（press-records.mjs aggregateSeries）。
const shown = computed(() =>
  query.value.trim() ? filtered.value : aggregateSeries(filtered.value),
);
const groups = computed(() => {
  const byYear = new Map();
  for (const item of shown.value) {
    const key = item.date.slice(0, 4);
    if (!byYear.has(key)) byYear.set(key, []);
    byYear.get(key).push(item);
  }
  return [...byYear].map(([year, items]) => ({
    year,
    items,
    count: items.reduce((n, i) => n + 1 + (i.seriesMembers?.length || 0), 0),
  }));
});
const reset = () => {
  const { q, year, format, kind, ...rest } = route.query;
  router.replace({ path: route.path, query: rest, hash: route.hash });
};
const lead = records.find((r) => r.id === 50),
  secondary = [63, 10].map((id) => records.find((r) => r.id === id));
const localized = (item, key) => {
  const language = zh.value ? "zh" : "en";
  if (!zh.value && item[key + "En"]) return item[key + "En"];
  const display =
    key === "title"
      ? item.displayTitle
      : key === "excerpt"
        ? item.displayExcerpt
        : null;
  return display?.[language] || item[key];
};
const titleLanguage = (item) =>
  /[\u3400-\u9fff]/.test(localized(item, "title")) ? "zh-Hant" : "en";
const bodyLanguage = (item) =>
  /[\u3400-\u9fff]/.test(localized(item, "excerpt")) ? "zh-Hant" : "en";
</script>
<style scoped>
.page-press {
  max-width: 1640px;
  margin: auto;
  padding: 70px 5vw 110px;
  background: #080808;
  color: #f2eee5;
  text-align: left;
  font-family: var(--font-body);
  overflow-wrap: anywhere;
}
.page-press a {
  color: inherit;
  text-decoration: none;
}
.page-press a:hover {
  color: var(--gold, #d9c267);
}
.page-press a:focus-visible,
.page-press button:focus-visible,
.page-press input:focus-visible,
.page-press select:focus-visible {
  outline: 2px solid var(--gold, #d9c267);
  outline-offset: 5px;
}
.press-kicker {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #b8b3a9;
  margin: 0 0 24px;
}
.press-heading h1 {
  font-family: var(--font-slab), var(--font-display);
  font-size: clamp(54px, 7vw, 108px);
  font-weight: 400;
  line-height: 1.04;
  letter-spacing: -0.025em;
  margin: 0 0 32px;
}
.page-press:lang(zh-Hant) .press-heading h1 {
  font-family: var(--font-display);
  font-size: clamp(44px, 6vw, 82px);
  line-height: 1.2;
}
.press-heading-bottom {
  display: flex;
  justify-content: space-between;
  gap: 32px;
  align-items: end;
  margin-bottom: 64px;
}
.press-heading-bottom p {
  max-width: 52ch;
  color: #c0bbb1;
  font-size: 18px;
  line-height: 1.8;
  margin: 0;
}
.press-heading-bottom a {
  font-size: 13px;
  white-space: nowrap;
  border-bottom: 1px solid #777;
  padding: 12px 0;
}
.press-heading-bottom a span {
  font-size: 11px;
  margin-left: 12px;
}
.press-opening {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(0, 1fr);
  gap: 64px;
  margin-bottom: 110px;
}
.press-portrait {
  margin: 0;
  min-width: 0;
}
.press-portrait img {
  width: 100%;
  height: auto;
  max-height: 760px;
  object-fit: contain;
  display: block;
  background: #111;
}
.press-portrait figcaption {
  color: #aaa49a;
  font-size: 11px;
  margin-top: 14px;
  line-height: 1.6;
}
.press-source {
  display: block;
  color: #b8b3a9;
  font-size: 12px;
  line-height: 1.7;
}
.press-lead {
  display: block;
  border-top: 1px solid #777;
  padding: 28px 0 34px;
}
.press-lead h2 {
  font-family: var(--font-slab), var(--font-display);
  font-size: clamp(34px, 3.6vw, 54px);
  font-weight: 400;
  line-height: 1.1;
  margin: 18px 0;
}
.press-lead p {
  font-size: 17px;
  line-height: 1.8;
  color: #c0bbb1;
  max-width: 48ch;
}
.press-action {
  display: block;
  font-size: 12px;
  margin-top: 22px;
  color: #d9c267;
}
.press-secondary {
  display: block;
  border-top: 1px solid #45423c;
  padding: 24px 0;
}
.press-secondary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px;
  gap: 24px;
  align-items: start;
}
.press-secondary-thumb {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  background: #151411;
  margin-top: 4px;
}
.press-secondary h3 {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 400;
  line-height: 1.6;
  margin: 12px 0;
}
.press-secondary .press-action {
  margin-top: 12px;
}
.press-archive {
  scroll-margin-top: 100px;
}
.press-archive-heading {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  border-top: 1px solid #777;
  padding-top: 30px;
  margin-bottom: 40px;
}
.press-archive-heading > p:last-child {
  max-width: 50ch;
  font-size: 13px;
  color: #aaa49a;
  line-height: 1.7;
  margin: 0;
}
.press-archive-layout {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 76px;
}
.press-tools {
  align-self: start;
  position: sticky;
  top: 100px;
}
.press-tools label {
  display: grid;
  gap: 10px;
  font-size: 12px;
  margin-bottom: 26px;
}
.press-tools input,
.press-tools select {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  border: 0;
  background: var(--bg2);
  color: inherit;
  padding: 10px 14px;
  font-size: 14px;
  border-radius: var(--r-md);
}
.press-tools fieldset {
  padding: 0;
  margin: 0 0 28px;
  border: 0;
  min-width: 0;
}
.press-tools legend {
  font-size: 12px;
  margin-bottom: 12px;
}
.press-tools fieldset button {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid #37342f;
  background: transparent;
  color: #b8b3a9;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
}
.press-tools fieldset button[aria-pressed="true"] {
  color: var(--gold);
}
.press-tools fieldset button span {
  font-size: 11px;
  color: #aaa49a;
}
.press-reset,
.press-empty button {
  background: var(--tint-2);
  color: var(--gold);
  border: 0;
  border-radius: var(--r-pill);
  padding: 10px 18px;
  min-height: 44px;
  cursor: pointer;
  font-size: 13px;
  transition: var(--t-hover);
}
.press-reset:hover,
.press-empty button:hover {
  background: var(--tint-3);
}
.press-result-count {
  font-size: 12px;
  color: #b8b3a9;
  margin-top: 24px;
}
.press-year-group + .press-year-group {
  margin-top: 36px;
}
.press-year-heading {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin: 0 0 28px;
  padding-bottom: 12px;
  border-bottom: 1px solid #777;
  font-family: var(--font-slab), var(--font-display);
  font-size: 28px;
  font-weight: 400;
  line-height: 1;
}
.press-year-heading small {
  font-family: var(--font-body);
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #aaa49a;
}
.press-record {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 248px;
  gap: 44px;
  align-items: start;
  padding: 0 0 30px;
  margin-bottom: 30px;
  border-bottom: 1px solid #45423c;
}
.press-year-group .press-record:last-child {
  border-bottom: 0;
  margin-bottom: 0;
}
.press-record-aside {
  display: grid;
  gap: 10px;
}
.press-record-media {
  display: block;
  aspect-ratio: 3 / 2;
  overflow: hidden;
  background: #151411;
}
.press-record-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition:
    transform 0.5s ease,
    opacity 0.3s ease;
  opacity: 0.92;
}
.press-record-media.is-contain img {
  object-fit: contain;
  padding: 10px;
}
.press-record:hover .press-record-media img {
  transform: scale(1.03);
  opacity: 1;
}
.press-record:hover .press-record-media.is-contain img {
  transform: none;
}
.press-record-date {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  font-size: 12px;
  color: #b8b3a9;
  line-height: 1.6;
}
.press-record-date > span {
  font-size: 11px;
  color: #aaa49a;
}
.press-record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  color: #b8b3a9;
  font-size: 12px;
  line-height: 1.6;
}
.press-kind {
  color: #d9c267;
}
.press-record h3 {
  font-family: var(--font-display);
  font-size: clamp(22px, 2vw, 28px);
  font-weight: 400;
  line-height: 1.45;
  margin: 12px 0;
}
.press-record h3 span {
  font-family: var(--font-body);
  font-size: 15px;
  white-space: nowrap;
}
.press-record-main > p {
  font-size: 15px;
  line-height: 1.9;
  color: #b8b3a9;
  max-width: 62ch;
  margin: 0 0 12px;
}
.press-series {
  margin-top: 14px;
  border-top: 1px solid rgba(170, 164, 154, 0.25);
  padding-top: 10px;
}
.press-series-label {
  color: #aaa49a;
  font-size: 12px;
  line-height: 1.6;
  margin: 0 0 8px;
}
.press-series-label span {
  color: inherit;
}
.press-series ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}
.press-series a {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  align-items: center;
  color: inherit;
  text-decoration: none;
}
.press-series img {
  width: 72px;
  height: 48px;
  align-self: start;
  object-fit: cover;
  background: rgba(170, 164, 154, 0.12);
}
.press-series img.is-contain {
  object-fit: contain;
}
.press-series a > span {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 10px;
  align-items: baseline;
  font-size: 13px;
  line-height: 1.5;
  min-width: 0;
}
.press-series b {
  font-weight: 500;
}
.press-series time {
  color: #aaa49a;
  font-size: 11px;
  white-space: nowrap;
}
.press-series em {
  grid-column: 1 / -1;
  font-style: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-width: 0;
}
.press-series a:hover em,
.press-series a:focus-visible em {
  color: var(--gold, #d9c267);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.press-original-title {
  color: #aaa49a;
  font-size: 12px;
  line-height: 1.7;
  margin-top: 10px;
}
.press-original-title summary {
  cursor: pointer;
  min-height: 32px;
}
.press-original-title p {
  margin: 6px 0;
}
.press-record small {
  color: #aaa49a;
  font-size: 11px;
  line-height: 1.6;
}
.press-empty {
  padding: 30px 0 80px;
  min-height: 260px;
}
.press-empty h2 {
  font-family: var(--font-display);
  font-size: 26px;
}
.press-empty p {
  color: #b8b3a9;
  line-height: 1.8;
}
.press-resources {
  border-top: 1px solid #777;
  margin-top: 90px;
  padding-top: 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
}
.press-resources h2 {
  font-family: var(--font-display);
  font-size: 32px;
  font-weight: 400;
  line-height: 1.5;
}
.press-resources p:not(.press-kicker) {
  font-size: 15px;
  line-height: 1.9;
  color: #b8b3a9;
  max-width: 50ch;
}
.press-resources nav {
  display: grid;
  align-content: start;
}
.press-resources nav a {
  padding: 18px 0;
  border-bottom: 1px solid #45423c;
  font-size: 17px;
  min-height: 44px;
}
@media (max-width: 991px) {
  .press-opening {
    gap: 36px;
  }
  .press-archive-layout {
    grid-template-columns: 190px minmax(0, 1fr);
    gap: 36px;
  }
  .press-record {
    grid-template-columns: minmax(0, 1fr) 180px;
    gap: 28px;
  }
  .press-heading-bottom {
    align-items: start;
  }
  .press-heading-bottom p {
    font-size: 16px;
  }
}
@media (max-width: 640px) {
  .page-press {
    padding: 40px 22px 70px;
  }
  .press-heading-bottom {
    display: block;
    margin-bottom: 36px;
  }
  .press-heading-bottom a {
    display: inline-block;
    margin-top: 18px;
  }
  .press-opening {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-bottom: 60px;
  }
  .press-portrait img {
    max-height: 440px;
  }
  .press-lead h2 {
    font-size: 38px;
  }
  .press-secondary h3 {
    font-size: 20px;
  }
  .press-archive-heading {
    display: block;
  }
  .press-archive-heading .press-kicker {
    margin-bottom: 15px;
  }
  .press-archive-layout {
    display: block;
  }
  .press-tools {
    position: static;
    margin-bottom: 40px;
  }
  .press-tools fieldset {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .press-tools fieldset button {
    width: auto;
    gap: 12px;
    border: 0;
    border-radius: var(--r-pill);
    background: var(--tint-2);
    padding: 10px 16px;
  }
  .press-tools fieldset button[aria-pressed="true"] {
    background: var(--ink);
    color: #000;
  }
  .press-tools fieldset button[aria-pressed="true"] span {
    color: #000;
  }
  .press-record {
    grid-template-columns: minmax(0, 1fr) 108px;
    gap: 16px;
    margin-bottom: 24px;
    padding-bottom: 24px;
  }
  .press-record h3 {
    font-size: 20px;
  }
  .press-record-main > p {
    font-size: 14px;
    line-height: 1.8;
  }
  .press-record-date {
    display: grid;
    gap: 2px;
    font-size: 11px;
  }
  .press-record-media.is-contain img {
    padding: 4px;
  }
  .press-secondary {
    grid-template-columns: minmax(0, 1fr) 96px;
    gap: 16px;
  }
  .press-year-heading {
    font-size: 24px;
  }
  .press-resources {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 60px;
  }
  .press-resources h2 {
    font-size: 28px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .page-press * {
    scroll-behavior: auto !important;
    transition: none !important;
    animation: none !important;
  }
}
</style>
